import { Controller } from "@hotwired/stimulus"

// Tracks Turbo navigation events (real HTTP requests) to mirror Devise's
// server-side session timeout. Shows a warning modal before expiry with
// an animated countdown ring and colour-state transitions.
//
// No keepalive pings during normal use — Turbo requests already reset
// Devise's last_request_at. "Stay logged in" sends a single keepalive.
//
export default class extends Controller {
  static values = {
    timeout: { type: Number, default: 1800 },
    warning: { type: Number, default: 120 },
    keepaliveUrl: { type: String, default: "/session/keepalive" },
    logoutUrl: { type: String, default: "/session/expire" },
    loginUrl: { type: String, default: "/users/sign_in" }
  }

  static targets = ["modal", "border", "ringFill", "ringTime", "actions", "signedOut", "stayBtn", "stayBtnContent"]

  CIRCUMFERENCE = 2 * Math.PI * 52 // 326.7

  connect() {
    this.lastActivity = Date.now()
    this.warningVisible = false
    // Ensure modal is hidden on (re)connect (e.g., after Turbo cache restore)
    this.modalTarget.classList.add("hidden")

    // Reset timer on Turbo navigations — these are real HTTP requests that
    // also reset Devise's last_request_at, so client and server stay in sync.
    this._onTurboRequest = () => {
      if (this.warningVisible) return
      this.lastActivity = Date.now()
    }
    document.addEventListener("turbo:before-fetch-request", this._onTurboRequest)

    // Browsers throttle/freeze setInterval in backgrounded tabs, so the idle
    // check can't run while the tab is hidden — meaning it may miss the warning
    // and expiry entirely. Re-check the instant the tab becomes visible again
    // so a stale session bounces to login before the user can act on it.
    this._onVisibility = () => {
      if (document.visibilityState === "visible") this._checkIdle()
    }
    document.addEventListener("visibilitychange", this._onVisibility)

    this._checkInterval = setInterval(() => this._checkIdle(), 5000)
  }

  disconnect() {
    document.removeEventListener("turbo:before-fetch-request", this._onTurboRequest)
    document.removeEventListener("visibilitychange", this._onVisibility)
    clearInterval(this._checkInterval)
    clearInterval(this._countdownInterval)
  }

  // ── Idle detection ──

  _checkIdle() {
    const idleSeconds = (Date.now() - this.lastActivity) / 1000
    const warningAt = this.timeoutValue - this.warningValue

    if (idleSeconds >= this.timeoutValue) {
      this._expire()
    } else if (idleSeconds >= warningAt && !this.warningVisible) {
      this._showWarning()
    }
  }

  // ── Warning modal ──

  _showWarning() {
    this.warningVisible = true

    // Reset to active state (in case re-shown after a previous expiry)
    this.actionsTarget.style.display = ""
    this.signedOutTarget.style.display = "none"
    this._resetStayBtn()
    this.borderTarget.classList.remove("st-low-time", "st-critical-time")

    // Set initial countdown + ring before revealing
    this._updateCountdown()
    this.modalTarget.classList.remove("hidden")

    this._countdownInterval = setInterval(() => {
      const remaining = this._getRemaining()
      if (remaining <= 0) {
        clearInterval(this._countdownInterval)
        this._expire()
        return
      }
      this._updateCountdown()
    }, 1000)
  }

  _hideWarning() {
    this.warningVisible = false
    clearInterval(this._countdownInterval)
    this.modalTarget.classList.add("hidden")
    this.borderTarget.classList.remove("st-low-time", "st-critical-time")
  }

  // ── Countdown ring ──

  _getRemaining() {
    return Math.max(0, this.timeoutValue - (Date.now() - this.lastActivity) / 1000)
  }

  _updateCountdown() {
    const remaining = this._getRemaining()
    const fraction = Math.min(remaining / this.warningValue, 1)

    // Ring arc
    this.ringFillTarget.style.strokeDashoffset = this.CIRCUMFERENCE * (1 - fraction)

    // Digital readout
    const mins = Math.floor(remaining / 60)
    const secs = Math.floor(remaining % 60)
    this.ringTimeTarget.textContent = `${mins}:${secs.toString().padStart(2, "0")}`

    // Colour states (scaled to warning duration)
    const quarter = this.warningValue * 0.25
    const half = this.warningValue * 0.5
    if (remaining <= quarter) {
      this.borderTarget.classList.remove("st-low-time")
      this.borderTarget.classList.add("st-critical-time")
    } else if (remaining <= half) {
      this.borderTarget.classList.add("st-low-time")
      this.borderTarget.classList.remove("st-critical-time")
    } else {
      this.borderTarget.classList.remove("st-low-time", "st-critical-time")
    }
  }

  // ── Session expiry ──

  _expire() {
    clearInterval(this._checkInterval)
    clearInterval(this._countdownInterval)

    // Show signed-out state in the modal
    this.actionsTarget.style.display = "none"
    this.signedOutTarget.style.display = "flex"

    // Ensure modal is visible (covers the _checkIdle path that skips _showWarning)
    if (!this.warningVisible) {
      this.modalTarget.classList.remove("hidden")
    }

    // Destroy session server-side, then redirect
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.logoutUrlValue, {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": csrfToken,
        "X-Requested-With": "XMLHttpRequest"
      }
    }).finally(() => {
      setTimeout(() => {
        window.location.replace(this.loginUrlValue)
      }, 1500)
    })
  }

  // ── Actions ──

  stayLoggedIn() {
    clearInterval(this._countdownInterval)
    this.lastActivity = Date.now()
    this._hideWarning()
    this._resetStayBtn()

    // Single keepalive ping to reset Devise timer
    fetch(this.keepaliveUrlValue, {
      method: "GET",
      headers: { "X-Requested-With": "XMLHttpRequest" }
    }).then(response => {
      if (!response.ok) this._expire()
    }).catch(() => {
      this._expire()
    })
  }

  signOut() {
    clearInterval(this._countdownInterval)
    this._expire()
  }

  _resetStayBtn() {
    this.stayBtnContentTarget.innerHTML = `
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
      Stay logged in
    `
    this.stayBtnTarget.style.pointerEvents = ""
  }
}
